import React, { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, PanelLeft, Plus, Search, Table2 } from 'lucide-react';
import { logDataModels } from '../dataModelsLogger';
import type { DataModel, ModelTable, Space } from '../dataModelsTypes';
import type { SpaceOrderPlace } from '../dataModelsUtils';
import { iconBtn, ModelIconTile, searchClass, SpaceIconTile, surfaces } from './dataModelsUi';

interface SpaceNode {
  space: Space;
  models: Array<{ model: DataModel; tables: ModelTable[] }>;
}

export default function SpacesPanel({
  isLight,
  nodes,
  query,
  onQuery,
  selectedSpaceId,
  expandedSpaceIds,
  expandedModelIds,
  onSelectSpace,
  onToggleSpace,
  onToggleModel,
  onSelectTable,
  onSelectModel,
  onAddModel,
  onAddAsset,
  selectedTableId,
  selectedModelId,
  loading,
  railOpen,
  onRailOpen,
  onReorderSpaces,
}: {
  isLight: boolean;
  nodes: SpaceNode[];
  query: string;
  onQuery: (value: string) => void;
  selectedSpaceId: string | null;
  expandedSpaceIds: string[];
  expandedModelIds: string[];
  onSelectSpace: (spaceId: string) => void;
  onToggleSpace: (spaceId: string) => void;
  onToggleModel: (modelId: string) => void;
  onSelectModel: (spaceId: string, modelId: string) => void;
  onAddModel: (spaceId: string) => void;
  onAddAsset: (spaceId: string, modelId: string) => void;
  onSelectTable: (spaceId: string, modelId: string, tableId: string) => void;
  selectedTableId: string | null;
  selectedModelId: string | null;
  loading?: boolean;
  railOpen: boolean;
  onRailOpen: (open: boolean) => void;
  onReorderSpaces?: (draggedId: string, targetId: string, place: SpaceOrderPlace) => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<{ id: string; place: SpaceOrderPlace } | null>(null);
  const draggingIdRef = useRef<string | null>(null);
  const canReorder = Boolean(onReorderSpaces) && nodes.length > 1 && !query.trim();
  const dropLine = isLight ? 'bg-[#1B6CA8]' : 'bg-sky-300';

  const clearDrag = () => {
    draggingIdRef.current = null;
    setDraggingId(null);
    setDropHint(null);
  };

  const placeFromPointer = (event: React.DragEvent<HTMLElement>): SpaceOrderPlace => {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
  };

  const toggleRail = () => {
    logDataModels('app', 'vault_rail_toggled', { open: !railOpen });
    onRailOpen(!railOpen);
  };

  if (!railOpen) {
    return (
      <aside className={`flex h-full w-11 shrink-0 flex-col items-center border-r pt-3 ${ui.border} ${ui.rail}`}>
        <button type="button" className={iconBtn(isLight)} title="Show WorkSpaces" aria-label="Show WorkSpaces" onClick={toggleRail}>
          <PanelLeft size={16} strokeWidth={1.75} />
        </button>
      </aside>
    );
  }

  return (
    <aside className={`flex h-full min-h-0 w-[272px] shrink-0 flex-col overflow-hidden border-r ${ui.border} ${ui.rail}`}>
      <div className={`flex shrink-0 items-center justify-between gap-2 px-3 pb-2 pt-3 ${ui.railHeader}`}>
        <h2 className={`px-1 text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>WorkSpaces</h2>
        <button type="button" className={iconBtn(isLight)} title="Hide WorkSpaces" aria-label="Hide WorkSpaces" onClick={toggleRail}>
          <PanelLeft size={16} strokeWidth={1.75} />
        </button>
      </div>

      <div className="shrink-0 px-3 pb-2">
        <div className="relative">
          <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
          <input value={query} onChange={(event) => onQuery(event.target.value)} placeholder="Type to search" className={searchClass(isLight)} />
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden overscroll-contain px-1.5 py-1">
          {loading ? (
            <p className={`px-3 py-6 text-sm ${muted}`}>Loading WorkSpaces…</p>
          ) : nodes.length === 0 ? (
            <p className={`px-3 py-6 text-sm ${muted}`}>{query.trim() ? 'No WorkSpaces match this search.' : 'No WorkSpaces yet.'}</p>
          ) : (
            nodes.map(({ space, models }) => {
              const expanded = expandedSpaceIds.includes(space.id);
              const selected = selectedSpaceId === space.id && !selectedModelId && !selectedTableId;
              const dragging = draggingId === space.id;
              const hint = dropHint?.id === space.id && dropHint.id !== draggingId ? dropHint.place : null;
              return (
                <div key={space.id} className="mb-0.5">
                  <div
                    className={`group relative flex w-full items-center gap-1.5 rounded px-1.5 py-1.5 text-left text-[13px] ${
                      dragging ? 'opacity-50 [&_[data-space-grip]]:opacity-100' : ''
                    } ${selected ? ui.selected : isLight ? 'text-[#1C1C1C] hover:bg-[#F4F6F8]' : 'text-slate-100 hover:bg-white/5'}`}
                    onDragOver={(event) => {
                      const draggedId = draggingIdRef.current;
                      if (!canReorder || !draggedId || draggedId === space.id) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      const place = placeFromPointer(event);
                      setDropHint((current) => (current?.id === space.id && current.place === place ? current : { id: space.id, place }));
                    }}
                    onDragLeave={(event) => {
                      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                      setDropHint((current) => (current?.id === space.id ? null : current));
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const draggedId = draggingIdRef.current;
                      if (!canReorder || !onReorderSpaces || !draggedId || draggedId === space.id) {
                        clearDrag();
                        return;
                      }
                      onReorderSpaces(draggedId, space.id, placeFromPointer(event));
                      clearDrag();
                    }}
                  >
                    {hint ? (
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute left-1.5 right-1.5 h-0.5 rounded-full ${dropLine} ${
                          hint === 'before' ? 'top-0' : 'bottom-0'
                        }`}
                      />
                    ) : null}
                    <button
                      type="button"
                      title={expanded ? `Collapse ${space.name}` : `Expand ${space.name}`}
                      aria-label={expanded ? `Collapse ${space.name}` : `Expand ${space.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleSpace(space.id);
                      }}
                      className={`inline-flex h-4 w-4 items-center justify-center ${muted}`}
                    >
                      {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </button>
                    <button
                      type="button"
                      title={space.name}
                      aria-label={space.name}
                      onClick={() => onSelectSpace(space.id)}
                      className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                    >
                      <SpaceIconTile icon={space.icon} isLight={isLight} box="h-5 w-5" />
                      <span className="min-w-0 flex-1 truncate font-medium">{space.name}</span>
                    </button>
                    <button
                      type="button"
                      aria-label={`Add catalog in ${space.name}`}
                      title={`Add catalog in ${space.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onAddModel(space.id);
                      }}
                      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 ${
                        isLight ? 'text-[#4B5563] hover:bg-[#E6E8EB]' : 'text-slate-200 hover:bg-white/10'
                      }`}
                    >
                      <Plus size={13} strokeWidth={2} />
                    </button>
                    {canReorder ? (
                      <span
                        role="button"
                        tabIndex={0}
                        draggable
                        aria-label={`Reorder ${space.name}`}
                        title={`Drag to reorder ${space.name}`}
                        onClick={(event) => event.stopPropagation()}
                        onDragStart={(event) => {
                          event.stopPropagation();
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', space.id);
                          draggingIdRef.current = space.id;
                          setDraggingId(space.id);
                          setDropHint(null);
                        }}
                        onDragEnd={clearDrag}
                        data-space-grip=""
                        className={`inline-flex h-5 w-5 shrink-0 cursor-grab select-none items-center justify-center rounded opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 active:cursor-grabbing ${
                          isLight ? 'text-[#9AA1AB] hover:bg-[#E6E8EB] hover:text-[#5C6570]' : 'text-slate-500 hover:bg-white/10 hover:text-slate-300'
                        }`}
                      >
                        <GripVertical size={13} strokeWidth={2} />
                      </span>
                    ) : null}
                  </div>

                  {expanded ? (
                    <div className="ml-3">
                      {models.map(({ model, tables }) => {
                        const modelOpen = expandedModelIds.includes(model.id);
                        const modelSelected = selectedModelId === model.id && !selectedTableId;
                        return (
                          <div key={model.id}>
                            <div
                              className={`group/model flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[13px] ${
                                modelSelected ? ui.selected : isLight ? 'text-[#1C1C1C] hover:bg-[#F4F6F8]' : 'text-slate-200 hover:bg-white/5'
                              }`}
                            >
                              <button
                                type="button"
                                title={modelOpen ? `Collapse ${model.name}` : `Expand ${model.name}`}
                                aria-label={modelOpen ? `Collapse ${model.name}` : `Expand ${model.name}`}
                                onClick={() => onToggleModel(model.id)}
                                className={`inline-flex h-4 w-4 items-center justify-center ${muted}`}
                              >
                                {modelOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </button>
                              <button
                                type="button"
                                title={model.name}
                                aria-label={model.name}
                                onClick={() => onSelectModel(space.id, model.id)}
                                className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                              >
                                <ModelIconTile isLight={isLight} box="h-5 w-5" />
                                <span className="min-w-0 flex-1 truncate">{model.name}</span>
                              </button>
                              <button
                                type="button"
                                aria-label={`Add asset in ${model.name}`}
                                title={`Add asset in ${model.name}`}
                                onClick={() => onAddAsset(space.id, model.id)}
                                className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded opacity-0 transition-opacity group-hover/model:opacity-100 focus-visible:opacity-100 ${
                                  isLight ? 'text-[#4B5563] hover:bg-[#E6E8EB]' : 'text-slate-200 hover:bg-white/10'
                                }`}
                              >
                                <Plus size={13} strokeWidth={2} />
                              </button>
                            </div>
                            {modelOpen ? (
                              <div className="ml-5">
                                {tables.map((table) => (
                                  <button
                                    key={table.id}
                                    type="button"
                                    title={table.name}
                                    aria-label={table.name}
                                    onClick={() => onSelectTable(space.id, model.id, table.id)}
                                    className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[13px] ${
                                      selectedTableId === table.id
                                        ? ui.selected
                                        : isLight
                                          ? 'text-[#3C4048] hover:bg-[#F4F6F8]'
                                          : 'text-slate-300 hover:bg-white/5'
                                    }`}
                                  >
                                    <Table2 size={13} className={muted} />
                                    <span className="truncate font-mono text-[12px]">{table.name}</span>
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>
    </aside>
  );
}
